import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { UserToken } from '../../models/user.model';
import { ApiDigitalAdminNotification } from '../../services/admin/notification.service';
import { ApiDigitalAuthInvitation } from '../../services/auth/invitation.service';
import { Error } from '../../models/error.model';
import { Message } from '../../models/message.model';
import { Notification } from '../../models/notification.model';
import { modalHide } from '../../utils/modal.util';
import { toastError, toastSuccess } from '../../utils/toast.util';
import { imageUser } from '../../scripts/image.value.script';
import { getError, getMessage } from '../../languages/translations/response';
import { errorWebHandler } from '../../scripts/error.web.handler.script';
import { datetimeFormat12h } from '../../scripts/date.time.format.script';
import { authApiKey } from '../../scripts/authentication.script';

import AppLoading from '../Loading';

export interface AppModalNotificationViewProps {
  userLoggedIn: UserToken | null,
  notification: Notification | null,
  loadNotificationsUnread: Function
};

let errorResponse: Error, messageResponse: Message;

const AppModalNotificationView: React.FunctionComponent<AppModalNotificationViewProps> = ({userLoggedIn, notification, loadNotificationsUnread}) => {
  const {lang} = StateLanguage()
  const navigate = useNavigate()

  const [loadIndicator, setLoadIndicator] = useState({view: 'off', close: 'off'})

  const resolveActionUrl = async (actionUrl: string): Promise<string> => {
    const invitationMatch = actionUrl.match(/\/invitations\/accept\/(.+)/)
    if (!invitationMatch) return actionUrl

    const invitationId = invitationMatch[1]
    const apiKey = authApiKey()
    if (!apiKey) return actionUrl

    try {
      const response = await ApiDigitalAuthInvitation.getMyInvitations(apiKey)
      const invList = Array.isArray(response.data) ? response.data : (response.data?.data ?? response.data?.invitations ?? [])
      if (response.status === 200 && Array.isArray(invList)) {
        const invitation = invList.find((inv: any) => inv.id === invitationId)
        if (invitation?.token) {
          return `/access/invitation/company/${invitation.token}`
        }
      }
    } catch {}

    return actionUrl
  }

  const submitNotificationView = async () => {
    if (!notification) return null

    setLoadIndicator({view: 'on', close: 'off'})

    await ApiDigitalAdminNotification.putNotificationRead(notification.id).then( async (response) => {
      if (response.status === 200) {
        messageResponse = response.data
        toastSuccess(getMessage(messageResponse.message, lang.code))
        const url = await resolveActionUrl(notification.action_url)
        navigate(url, {replace: true})
      } else {
        errorResponse = response.data
        toastError(getError(errorResponse.code, lang.code, errorResponse.message))
      }
      loadNotificationsUnread()
      modalHide('modal-notification-view')
    }).catch( (error) => {
      errorWebHandler(lang, error)
    }).finally(() => {
      setLoadIndicator({view: 'off', close: 'off'})
    })
  }

  const submitNotificationClose = async () => {
    if (notification && notification.status !== 'read') {
      setLoadIndicator({view: 'off', close: 'on'})

      await ApiDigitalAdminNotification.putNotificationRead(notification.id).then( (response) => {
        if (response.status === 200) {
          messageResponse = response.data
          toastSuccess(getMessage(messageResponse.message, lang.code))
          loadNotificationsUnread()
        } else {
          errorResponse = response.data
          toastError(getError(errorResponse.code, lang.code, errorResponse.message))
        }
        loadNotificationsUnread()
        modalHide('modal-notification-view')
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator({view: 'off', close: 'off'})
      })
    } else {
      modalHide('modal-notification-view')
      setLoadIndicator({view: 'off', close: 'off'})
    }
  }

  return (
    <div id="modal-notification-view" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-md">
        <div className="modal-content">
          { userLoggedIn && notification
            ?
            <>
              <div className="modal-body">
                <div className="mb-5">
                  <h2 className="text-center mb-5">{notification.title}</h2>
                  <div className="text-justify fs-6">{notification.message}</div>
                </div>
                <div className="bg-light mb-3">
                  <div className="d-flex flex-stack p-3">
                    <div className="d-flex align-items-center">
                      <div className="symbol symbol-50px">
                        <img src={imageUser(userLoggedIn.avatar)} alt={lang.labels.avatar} />
                      </div>
                      <div className="ms-5">
                        <div className="fs-6 fw-bold text-dark">{userLoggedIn.first_name} {userLoggedIn.last_name}</div>
                        <div className="fw-semibold text-muted">{userLoggedIn.email}</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="text-end fw-bolder fs-7">
                  {datetimeFormat12h(lang.code, notification.created_at)}
                </div>
              </div>
              <div className="modal-footer pt-0 border-0 justify-content-center align-items-end">
                <div className="mx-3">
                  <button className="btn btn-primary" type="button" data-kt-indicator={loadIndicator.view} onClick={submitNotificationView}>
                    <span className="indicator-label">{lang.labels.okGotIt}</span>
                    <span className="indicator-progress">
                      {lang.labels.pleaseWait}
                      <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                    </span>
                  </button>
                </div>
                <div className="mx-3">
                  <button className="btn btn-info" type="button" data-kt-indicator={loadIndicator.close} onClick={submitNotificationClose}>
                    <span className="indicator-label">{lang.labels.close}</span>
                    <span className="indicator-progress">
                      {lang.labels.pleaseWait}
                      <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                    </span>
                  </button>
                </div>
              </div>
            </>
            :
            <AppLoading></AppLoading>
          }
        </div>
      </div>
    </div>
  )
}

export default AppModalNotificationView;
